import { ChiptaElement, html, css } from 'chipta-libs/main.js';
import { csrfHiddenInput } from 'chipta-libs/snippets/csrf-hidden-input.js'
import { url, getQueryParam } from 'chipta-libs/utils/url.js';
import { accountAPI } from 'chipta-libs/api/account.js';
import 'chipta-libs/components/ticketshop-shell.js';
import 'chipta-libs/components/ticketshop-header.js';
import 'chipta-libs/components/styled-button.js';
import 'chipta-libs/components/info-box.js';
import 'chipta-libs/components/mark-down.js';
import { layoutStyles } from 'chipta-libs/styles/layout.js';
import { formStyles } from 'chipta-libs/styles/form.js';

class PasswordReset extends layoutStyles(formStyles(ChiptaElement)) {

  static get properties() {
    return {
      passwordResetMailSending: {type: Boolean},
      passwordResetMailSent: {type: Boolean}
    }
  }

  render() {

    return html`

      <ticketshop-shell>
        <ticketshop-header headertext=${gettext('password_reset')}>
          <div class="smaller">
            ${this.content}
          </div>
        </ticketshop-header>
      </ticketshop-shell>

    `;

  }

  get content() {

    if (jsVar('passwordResetted')) {
      return this.passwordResettedContent;
    } else if (jsVar('passwordResetUser').token) {

      if (jsVar('passwordResetUser').userId) {
        return this.passwordResetContent;
      } else {
        return this.invalidTokenContent;
      }

    } else {
      return this.requestPasswordResetContent;
    }

  }

  get requestPasswordResetContent() {

    return html`

      <div class="form">
        <info-box title=${gettext('how_to_reset_your_password')}>
          ${this.passwordResetRequestDescriptionText}
        </info-box>
      </div>

      <section class="content-box">

        <h2>${gettext('send_password_reset_mail')}</h2>

        ${this.passwordResetRequestWidget}

      </section>

      <div class="root">
      </div>

    `;

  }

  get passwordResetRequestWidget() {

    if (this.passwordResetMailSent) {

      return html`

        <mark-down .text=${this.passwordResetRequestSuccessMessage}></mark-down>

        <styled-button method="back">
          <button
            button-style="cancel"
            @click=${() => this.passwordResetMailSent = false}
            style="margin-top: 10px;"
          >
            ${gettext('back')}
          </button>
        </styled-button>

      `;

    }

    return html`

      <form @submit=${this.handleRequestPasswordResetFormSubmit}>

        <label class="field">

          <div class="input">

            <input
              type="email"
              id="forgot-password-email"
              placeholder=${gettext('mail')}
              pattern="${jsVar('emailRegex', '')}"
              value=${jsVar('userData').email}
              required
              ?disabled=${jsVar('userData').logged_in}
              autofocus
            ></input>

          </div>

          <div class="spread">

            <styled-button
              type="negative"
              href=${url(getQueryParam('previous')) || url('account/login/')}
            >
              ${gettext('cancel')}
            </styled-button>

            <styled-button method="action">
              <button type="submit">
                ${this.passwordResetRequestButtonText}
              </button>
            </styled-button>

          </div>

        </label>

      </form>
    `;

  }

  get passwordResetRequestDescriptionText() {
    if (jsVar('userData').logged_in) {
      return gettext('password_reset_request_description_logged_in');
    } else {
      return gettext('password_reset_request_description');
    }
  }

  get passwordResetRequestButtonText() {
    if (this.passwordResetMailSending) {
      return gettext('sending') + '...';
    } else {
      return gettext('send');
    }
  }

  get passwordResetRequestSuccessMessage() {

    const getTranslation = () => {
      if (jsVar('userData').logged_in) {
        return gettext('password_reset_email_sent_logged_in');
      } else {
        return gettext('password_reset_email_sent');
      }
    };

    return interpolate(
      getTranslation(),
      { email: this.passwordResetMailSent },
      true
    );

  }

  handleRequestPasswordResetFormSubmit(event) {

    event.preventDefault();

    const passwordResetMail = this.el('#forgot-password-email').value;

    if (!passwordResetMail) {
      // Ignore when submitted with empty email address field
      return;
    }

    this.passwordResetMailSending = true;
    this.passwordResetMailSent = false;
    this.passwordResetMailError = null;

    accountAPI.requestPasswordReset(passwordResetMail).finally(() => {
      this.passwordResetMailSending = false;
      this.passwordResetMailSent = passwordResetMail;
    });

  }

  get passwordResettedContent() {

    return html`

      <info-box title=${gettext('new_password_saved')}>
        <mark-down .text=${gettext('password_reset_success_message')}></mark-down>
      </info-box>

      ${this.loginOrPersonalDetailsButton}

    `;

  }

  get loginOrPersonalDetailsButton() {

    if (jsVar('userData').logged_in) {

      return html`
        <div class="content">
          <styled-button method="action" href=${url('account/personal-details/')}>
            ${gettext('personal_details')}
          </styled-button>
        </div>
      `;

    } else {

      return html`
        <div class="content">
          <styled-button method="action" href=${url('account/login/')}>
            ${gettext('login')}
          </styled-button>
        </div>
      `;

    }

  }

  get passwordResetContent() {

    return html`

      <info-box root>
        <mark-down .text=${this.passwordResetDescriptionText}></mark-down>
      </info-box>

      <section class="content-box">

        <form id="form" method="POST">

          ${this.passwordResetErrors}

          ${csrfHiddenInput()}

          <label class="field">

            <div class="label">${gettext('enter_new_password')}:</div>

            <div class="input">
              <input type="password" name="new_password" required autofocus />
            </div>

          </label>

          <label class="field">

            <div class="label">${gettext('repeat_new_password')}:</div>

            <div class="input">
              <input type="password" name="new_password_validation" required />
            </div>

          </label>

          <styled-button method="action">
            <button type="submit">${gettext('reset_password')}</button>
          </styled-button>

        </form>

      </section>

    `;

  }

  get passwordResetDescriptionText() {
    return interpolate(
      gettext('password_reset_description'),
      { email: jsVar('passwordResetUser').email },
      true
    );
  }

  get passwordResetErrors() {

    const errors = jsVar('errors');

    if (!errors || !Object.keys(errors).length) {
      return null;
    }

    return html`
      <div class="errors">
        ${Object.keys(errors).map(key => html`<div class="error">${errors[key]}</div>`)}
      </div>
    `;

  }

  get invalidTokenContent() {
    return html`
      <p>${gettext('opened_link_is_expired')}</p>
    `;
  }

  static get styles() {

    return css`

      :host {
        display: flex;
        min-height: 100%;
        font-size: 15px;
      }

      p:first-child {
        margin-top: 0;
      }

    `;

  }

}

customElements.define('password-reset', PasswordReset);
